import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';

import { useSuspendSubscriptionMutation } from 'api/main';
import { useCreateUserStepMutation } from 'api/tunnelSteps';
import { userSelectors, tunnelSelectors } from 'reducers';
import { ineligibleSet } from 'reducers/tunnel';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, Loader } from 'components';
import { Dialog, DialogActions, DialogContent, DialogContentText } from '@mui/material';
import * as icons from 'mdi-material-ui';

import makeStyles from '@mui/styles/makeStyles';
import { useTunnelPhases } from 'utils/hooks/tunnel';
import { useSmallScreen } from 'utils/hooks/ui';

const useStyles = makeStyles(theme => ({
    paper: {
        [theme.breakpoints.down('sm')]: {
            minWidth: 'unset',
        },
    },
    mainText: {
        color: theme.palette.common.black,
        fontSize: 22,
        lineHeight: '26px',
    },
    dialogContent: {
        padding: 30,
    },
    dialogActions: {
        display: 'flex',
        flexDirection: 'column',
    },
    button: {
        letterSpacing: '1.3px',
        '&:hover': {
            background: 'inherit',
        },
    },
    longLink: {
        width: '50%',
        [theme.breakpoints.down('sm')]: {
            width: '100%',
        },
    },
    buttonsContainer: {
        alignItems: 'center',
        justifyContent: 'flex-start',
        padding: theme.spacing(1.5, '30px'),
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
        },
    },
    centerButton: {
        justifyContent: 'center',
    },
    icon: {
        fontSize: 16,
    },
}));

const IneligibleDialog = props => {
    const { autoClose = true } = props;

    const classes = useStyles();
    const fullScreen = useSmallScreen('sm');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [, { isLoading: suspendLoader }] = useSuspendSubscriptionMutation({
        fixedCacheKey: 'suspend-subscription',
    });
    const [createUserStep, { isLoading: stepCreateLoader, data, isSuccess: createSuccess }] =
        useCreateUserStepMutation();

    const userSteps = useSelector(userSelectors.getUserTunnelSteps);
    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const {
        open = false,
        subMessage = texts.MERCI_DE_VOUS_RAPPROCHER,
        message,
        abandon = true,
        canConfirm = false,
        title = texts.VEUILLEZ_CHOISIR_UNE_OPTION,
        hasDialogActions = true,
        onConfirm,
        confirmMessage,
    } = useSelector(tunnelSelectors.getIneligibleStatus);
    const { phases } = useTunnelPhases();

    let { stepId } = useParams();
    stepId = +stepId;

    const handleClose = useCallback(() => {
        dispatch(ineligibleSet({ open: false }));
    }, [dispatch]);

    const handleConfirm = useCallback(() => {
        if (autoClose) {
            handleClose();
        }
        onConfirm();
    }, [onConfirm, handleClose, autoClose]);

    const handleAbandon = useCallback(() => {
        const userStepId = userSteps?.find(us => us.tunnelStep.step.id === stepId)?.id || null;
        if (!userStepId) {
            let currentTunnelStepId = null;
            for (let phase of phases) {
                const tunnelStep = phase.tunnelSteps.find(ts => ts.step.id === stepId);
                if (tunnelStep) {
                    currentTunnelStepId = tunnelStep.id;
                    break;
                }
            }
            if (currentTunnelStepId) {
                createUserStep({
                    tunnelStepId: currentTunnelStepId,
                    subscriptionId: tunnelSubscription?.id,
                });
            }
        } else {
            navigate(`/abandonner?userStepId=${userStepId}`);
        }
    }, [userSteps, stepId, phases, createUserStep, tunnelSubscription, navigate]);

    useEffect(() => {
        if (createSuccess) {
            navigate(`/abandonner?userStepId=${data?.id}`);
        }
    }, [createSuccess, data, navigate]);

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            classes={{ paper: classes.paper }}
            aria-labelledby="responsive-dialog-title"
            transitionDuration={{ enter: 200, exit: 0 }}
        >
            <CustomDialogTitle handleClose={handleClose} title={title} />
            <DialogContent classes={{ root: classes.dialogContent }}>
                <DialogContentText className={classes.mainText} gutterBottom>
                    {message}
                </DialogContentText>
                <DialogContentText>{subMessage}</DialogContentText>
            </DialogContent>
            {hasDialogActions && (
                <DialogActions
                    className={clsx(classes.buttonsContainer, {
                        [classes.centerButton]: !abandon,
                    })}
                >
                    {canConfirm && (
                        <ActionButton
                            className={clsx(classes.button, classes.longLink)}
                            onClick={handleConfirm}
                            disabled={suspendLoader}
                        >
                            <Loader loading={suspendLoader} size={20} color="inherit">
                                {confirmMessage} <icons.ArrowRight className={classes.icon} />
                            </Loader>
                        </ActionButton>
                    )}
                    {abandon && (
                        <CancelButton
                            className={classes.button}
                            onClick={handleAbandon}
                            disabled={stepCreateLoader}
                        >
                            {texts.ABANDONNER} <icons.ArrowRight className={classes.icon} />
                        </CancelButton>
                    )}
                    <ActionButton className={classes.button} onClick={handleClose}>
                        {texts.MODIFIER_LES_RESPONSES} <icons.ArrowRight className={classes.icon} />
                    </ActionButton>
                </DialogActions>
            )}
        </Dialog>
    );
};

IneligibleDialog.propTypes = {
    onAbandonner: PropTypes.func,
    autoClose: PropTypes.bool,
};

export default IneligibleDialog;
